import React from 'react';
import Plot from 'react-plotly.js';

interface RescueScoreData {
  target_gene: string;
  rescue_score: number;
  effectiveness: string;
  top_rescued_pathways?: Array<{
    pathway: string;
    rescue_percentage: number;
  }>;
  baseline_score?: number;
  knockdown_score?: number;
}

interface RescueScoreBarPlotProps {
  data: RescueScoreData;
  title?: string;
}

const RescueScoreBarPlot: React.FC<RescueScoreBarPlotProps> = ({
  data,
  title = "Knockdown Rescue Analysis"
}) => {
  if (!data || !data.target_gene || data.rescue_score === undefined || data.rescue_score === null) {
    return (
      <div className="origin-card p-4">
        <p className="text-[var(--text-secondary)]">No rescue score data available</p>
      </div>
    );
  }

  const { rescue_score, baseline_score, knockdown_score, effectiveness } = data;

  // Determine color based on effectiveness
  const getEffectivenessColor = (eff: string) => {
    if (eff === 'Highly Effective') return '#10b981'; // Green
    if (eff === 'Moderately Effective') return '#f59e0b'; // Yellow
    if (eff === 'Low Effectiveness') return '#ef4444'; // Red
    return '#9ca3af'; // Gray
  };

  // Main rescue score bar
  const rescueTrace = {
    x: [rescue_score],
    y: ['Rescue Score'],
    type: 'bar',
    orientation: 'h',
    marker: {
      color: getEffectivenessColor(effectiveness),
      line: {
        color: 'rgb(255, 255, 255)',
        width: 1
      }
    },
    text: [`${rescue_score.toFixed(1)}%`],
    textposition: 'inside',
    textfont: {
      size: 14,
      color: 'rgb(255, 255, 255)',
      weight: 'bold'
    },
    hovertemplate: `<b>Rescue Score</b><br>${rescue_score.toFixed(1)}%<br><b>${effectiveness}</b><extra></extra>`,
    name: 'Rescue Score'
  };

  // Pathway rescue bars (if available)
  const pathwayTraces = data.top_rescued_pathways?.slice(0, 5).map((pathway, idx) => {
    const rescuePercentage = pathway.rescue_percentage ?? 0;
    return {
      x: [rescuePercentage],
      y: [pathway.pathway.length > 40 ? pathway.pathway.substring(0, 40) + '...' : pathway.pathway],
      type: 'bar',
      orientation: 'h',
      marker: {
        color: 'rgba(59, 130, 246, 0.6)', // Blue with transparency
        line: {
          color: 'rgb(59, 130, 246)',
          width: 1
        }
      },
      text: [`${rescuePercentage.toFixed(1)}%`],
      textposition: 'inside',
      textfont: {
        size: 12,
        color: 'rgb(255, 255, 255)'
      },
      hovertemplate: `<b>${pathway.pathway}</b><br>${rescuePercentage.toFixed(1)}% rescue<extra></extra>`,
      name: pathway.pathway,
      showlegend: false
    };
  }) || [];

  return (
    <div className="origin-card p-6">
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-[var(--text-primary)]">{title}</h3>
        <div className="flex gap-4 text-sm text-[var(--text-secondary)] mt-1">
          <span>Target: <span className="text-[var(--origin-blue-500)] font-medium">{data.target_gene}</span></span>
          <span>Effectiveness: <span
            className="font-medium"
            style={{ color: getEffectivenessColor(effectiveness) }}
          >
            {effectiveness}
          </span></span>
        </div>
      </div>

      {/* Main Rescue Score */}
      <Plot
        data={[rescueTrace as any]}
        layout={{
          showlegend: false,
          xaxis: {
            title: 'Rescue Score (%)',
            titlefont: {
              size: 12,
              color: 'rgb(156, 163, 175)'
            },
            range: [0, 100],
            gridcolor: 'rgba(156, 163, 175, 0.2)',
            tickfont: {
              size: 11,
              color: 'rgb(156, 163, 175)'
            }
          },
          yaxis: {
            showticklabels: false,
            showgrid: false
          },
          paper_bgcolor: 'transparent',
          plot_bgcolor: 'transparent',
          margin: { l: 20, r: 40, t: 10, b: 40 },
          height: 100
        }}
        config={{
          displayModeBar: false
        }}
        style={{ width: '100%' }}
      />

      {/* Score Comparison (if baseline and knockdown available) */}
      {baseline_score !== undefined && baseline_score !== null &&
       knockdown_score !== undefined && knockdown_score !== null && (
        <div className="mt-4 p-4 bg-[var(--bg-secondary)] rounded-lg border border-[var(--border-primary)]">
          <p className="text-xs text-[var(--text-tertiary)] mb-2 font-semibold">Disease Score Comparison</p>
          <div className="flex items-center gap-4 text-sm">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-[#ef4444]"></div>
              <span className="text-[var(--text-secondary)]">
                Baseline: <span className="font-medium text-[#ef4444]">{baseline_score.toFixed(2)}</span>
              </span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-[#10b981]"></div>
              <span className="text-[var(--text-secondary)]">
                After Knockdown: <span className="font-medium text-[#10b981]">{knockdown_score.toFixed(2)}</span>
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-[var(--text-secondary)]">
                Δ: <span className="font-medium text-[var(--origin-blue-500)]">{(baseline_score - knockdown_score).toFixed(2)}</span>
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Top Rescued Pathways */}
      {data.top_rescued_pathways && data.top_rescued_pathways.length > 0 && (
        <div className="mt-4">
          <p className="text-sm font-semibold text-[var(--text-primary)] mb-2">Top Rescued Pathways</p>
          <Plot
            data={pathwayTraces as any}
            layout={{
              showlegend: false,
              xaxis: {
                title: 'Pathway Rescue (%)',
                titlefont: {
                  size: 12,
                  color: 'rgb(156, 163, 175)'
                },
                range: [0, 100],
                gridcolor: 'rgba(156, 163, 175, 0.2)',
                tickfont: {
                  size: 11,
                  color: 'rgb(156, 163, 175)'
                }
              },
              yaxis: {
                tickfont: {
                  size: 10,
                  color: 'rgb(156, 163, 175)'
                },
                showgrid: false
              },
              paper_bgcolor: 'transparent',
              plot_bgcolor: 'transparent',
              margin: { l: 200, r: 40, t: 10, b: 40 },
              height: Math.max(200, data.top_rescued_pathways.length * 40)
            }}
            config={{
              displayModeBar: false
            }}
            style={{ width: '100%' }}
          />
        </div>
      )}

      {/* Effectiveness Legend */}
      <div className="flex gap-6 text-xs text-[var(--text-tertiary)] mt-4 justify-center">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-[#10b981]"></div>
          <span>Highly Effective (&gt;70%)</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-[#f59e0b]"></div>
          <span>Moderate (40-70%)</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-[#ef4444]"></div>
          <span>Low (&lt;40%)</span>
        </div>
      </div>

      {/* Interpretation */}
      <div className="mt-4 p-4 bg-[var(--bg-secondary)] rounded-lg border border-[var(--border-primary)] text-sm text-[var(--text-secondary)]">
        <p className="font-semibold text-[var(--origin-blue-500)] mb-2">Interpretation:</p>
        <p>
          Knockdown of <span className="font-medium text-[var(--origin-blue-500)]">{data.target_gene}</span> shows{' '}
          <span className="font-medium" style={{ color: getEffectivenessColor(effectiveness) }}>
            {rescue_score.toFixed(1)}% rescue
          </span>{' '}
          of disease phenotype. This indicates{' '}
          {rescue_score >= 70 && "strong therapeutic potential with significant disease reversal."}
          {rescue_score >= 40 && rescue_score < 70 && "moderate therapeutic potential worth further investigation."}
          {rescue_score < 40 && "limited therapeutic potential; consider combination strategies."}
        </p>
      </div>
    </div>
  );
};

export default RescueScoreBarPlot;
